import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Truck,
  Plus,
  MapPin,
  Package,
  Clock,
  CheckCircle,
  Eye,
  Phone,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { saveToLocalDB, getFromLocalDB, addToSyncQueue } from '@/lib/offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const DELIVERY_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const DESTINATION_TYPES = {
  other_shop: 'Other Agrivet Shop',
  farm: 'Farm',
  customer: 'Direct Customer'
};

const NASDeliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewingDelivery, setViewingDelivery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    destinationType: 'other_shop',
    destinationName: '',
    destinationAddress: '',
    contactPerson: '',
    contactNumber: '',
    items: [],
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveryFee: 0,
    notes: ''
  });
  
  const [newItem, setNewItem] = useState({ productId: '', quantity: 0 });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    try {
      if (navigator.onLine) {
        // Load deliveries
        const deliveriesResponse = await axios.get(`${API}/api/nas/deliveries`);
        setDeliveries(deliveriesResponse.data);
        await saveToLocalDB('nas_deliveries', deliveriesResponse.data);
        
        // Load products for selection
        const productsResponse = await axios.get(`${API}/api/nas/products`);
        setProducts(productsResponse.data);
      } else {
        const localDeliveries = await getFromLocalDB('nas_deliveries');
        const localProducts = await getFromLocalDB('nas_products');
        if (localDeliveries) setDeliveries(localDeliveries);
        if (localProducts) setProducts(localProducts);
        toast.info('Offline mode - showing cached data');
      }
    } catch (error) {
      console.error('Error loading deliveries:', error);
      const localDeliveries = await getFromLocalDB('nas_deliveries');
      const localProducts = await getFromLocalDB('nas_products');
      if (localDeliveries) setDeliveries(localDeliveries);
      if (localProducts) setProducts(localProducts);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddItem = () => {
    if (!newItem.productId || newItem.quantity <= 0) {
      toast.error('Please select product and quantity');
      return;
    }
    
    const product = products.find(p => p.id === newItem.productId);
    if (!product) return;
    
    setFormData({
      ...formData,
      items: [...formData.items, {
        productId: newItem.productId,
        productName: product.name,
        quantity: newItem.quantity,
        unit: product.unitType?.includes('SACK') ? 'sack' : 'unit'
      }]
    });
    setNewItem({ productId: '', quantity: 0 });
  };
  
  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };
  
  const handleCreateDelivery = async () => {
    if (!formData.destinationName || formData.items.length === 0) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        const response = await axios.post(`${API}/api/nas/deliveries`, formData);
        const newDelivery = response.data;
        const updatedDeliveries = [newDelivery, ...deliveries];
        setDeliveries(updatedDeliveries);
        await saveToLocalDB('nas_deliveries', updatedDeliveries);
        toast.success('Delivery scheduled successfully');
      } else {
        const deliveryData = {
          ...formData,
          id: `local_${Date.now()}`,
          totalItems: formData.items.reduce((sum, item) => sum + item.quantity, 0),
          status: 'pending',
          createdAt: new Date().toISOString(),
          _pendingSync: true
        };
        const updatedDeliveries = [deliveryData, ...deliveries];
        setDeliveries(updatedDeliveries);
        await saveToLocalDB('nas_deliveries', updatedDeliveries);
        await addToSyncQueue({ type: 'CREATE_DELIVERY', data: deliveryData, timestamp: new Date().toISOString() });
        toast.success('Delivery scheduled (will sync when online)');
      }
      
      resetForm();
    } catch (error) {
      console.error('Error creating delivery:', error);
      toast.error(error.response?.data?.detail || 'Failed to create delivery');
    } finally {
      setIsSaving(false);
    }
  };
  
  const updateDeliveryStatus = async (deliveryId, newStatus) => {
    try {
      if (navigator.onLine) {
        await axios.put(`${API}/api/nas/deliveries/${deliveryId}/status?status=${newStatus}`);
      }
      
      const updatedDeliveries = deliveries.map(d =>
        d.id === deliveryId ? { ...d, status: newStatus, updatedAt: new Date().toISOString() } : d
      );
      setDeliveries(updatedDeliveries);
      await saveToLocalDB('nas_deliveries', updatedDeliveries);
      
      if (!navigator.onLine) {
        await addToSyncQueue({ type: 'UPDATE_DELIVERY', data: { id: deliveryId, status: newStatus } });
      }
      
      toast.success(`Delivery marked as ${DELIVERY_STATUS[newStatus].label}`);
    } catch (error) {
      console.error('Error updating delivery:', error);
      toast.error('Failed to update delivery status');
    }
  };
  
  const resetForm = () => {
    setFormData({
      destinationType: 'other_shop',
      destinationName: '',
      destinationAddress: '',
      contactPerson: '',
      contactNumber: '',
      items: [],
      deliveryDate: new Date().toISOString().split('T')[0],
      deliveryFee: 0,
      notes: ''
    });
    setIsAddModalOpen(false);
  };
  
  const filteredDeliveries = deliveries.filter(d =>
    statusFilter === 'all' || d.status === statusFilter
  );
  
  const pendingCount = deliveries.filter(d => d.status === 'pending').length;
  const inTransitCount = deliveries.filter(d => d.status === 'in_transit').length;
  const deliveredCount = deliveries.filter(d => d.status === 'delivered').length;
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Deliveries</h1>
          <p className="text-slate-500">Track deliveries to shops, farms, and customers ({deliveries.length} total)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            New Delivery
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">In Transit</p>
                <p className="text-xl font-bold">{inTransitCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Delivered</p>
                <p className="text-xl font-bold">{deliveredCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filter */}
      <div className="flex gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(DELIVERY_STATUS).map(([key, value]) => (
              <SelectItem key={key} value={key}>{value.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Deliveries List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Loading deliveries...</p>
          </div>
        ) : filteredDeliveries.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center">
              <Truck className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 mb-4">
                {deliveries.length === 0 ? 'No deliveries yet. Schedule your first delivery!' : 'No deliveries match your filter.'}
              </p>
              {deliveries.length === 0 && (
                <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" />
                  Schedule First Delivery
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredDeliveries.map(delivery => (
            <Card key={delivery.id} className={`bg-white ${delivery._pendingSync ? 'border-amber-300' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{delivery.destinationName}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${DELIVERY_STATUS[delivery.status]?.color}`}>
                        {DELIVERY_STATUS[delivery.status]?.label}
                      </span>
                      {delivery._pendingSync && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                          Pending Sync
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {DESTINATION_TYPES[delivery.destinationType]}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {delivery.totalItems || delivery.items?.length || 0} items
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {delivery.deliveryDate}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewingDelivery(delivery)}>
                      <Eye size={16} className="mr-1" /> View
                    </Button>
                    {delivery.status === 'pending' && (
                      <Button size="sm" onClick={() => updateDeliveryStatus(delivery.id, 'in_transit')}>
                        <Truck size={16} className="mr-1" /> Start
                      </Button>
                    )}
                    {delivery.status === 'in_transit' && (
                      <Button size="sm" className="bg-green-600" onClick={() => updateDeliveryStatus(delivery.id, 'delivered')}>
                        <CheckCircle size={16} className="mr-1" /> Complete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Create Delivery Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule New Delivery</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Destination Type</Label>
              <Select value={formData.destinationType} onValueChange={(v) => setFormData({...formData, destinationType: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="other_shop">Other Agrivet Shop</SelectItem>
                  <SelectItem value="farm">Farm</SelectItem>
                  <SelectItem value="customer">Direct Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Destination Name *</Label>
              <Input
                value={formData.destinationName}
                onChange={(e) => setFormData({...formData, destinationName: e.target.value})}
                placeholder="e.g., Agrivet Plus, Reyes Farm"
              />
            </div>
            
            <div>
              <Label>Address</Label>
              <Textarea
                value={formData.destinationAddress}
                onChange={(e) => setFormData({...formData, destinationAddress: e.target.value})}
                placeholder="Full delivery address"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input
                  value={formData.contactNumber}
                  onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
                  placeholder="09XX XXX XXXX"
                />
              </div>
            </div>
            
            {/* Items */}
            <div>
              <Label className="mb-2 block">Items to Deliver</Label>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-12 gap-2">
                  <Select 
                    value={newItem.productId} 
                    onValueChange={(v) => setNewItem({...newItem, productId: v})}
                  >
                    <SelectTrigger className="col-span-7">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="col-span-3"
                    type="number"
                    placeholder="Qty"
                    value={newItem.quantity || ''}
                    onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 0})}
                  />
                  <Button className="col-span-2" onClick={handleAddItem}>Add</Button>
                </div>
                
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded">
                        <span>{item.productName}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.quantity} {item.unit}s</span>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)}>×</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                />
              </div>
              <div>
                <Label>Delivery Fee (₱)</Label>
                <Input
                  type="number"
                  value={formData.deliveryFee || ''}
                  onChange={(e) => setFormData({...formData, deliveryFee: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Special instructions..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleCreateDelivery} disabled={isSaving} className="bg-green-600">
              {isSaving ? 'Scheduling...' : 'Schedule Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Delivery Modal */}
      <Dialog open={!!viewingDelivery} onOpenChange={() => setViewingDelivery(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delivery Details</DialogTitle>
          </DialogHeader>
          {viewingDelivery && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{viewingDelivery.destinationName}</p>
                  <p className="text-sm text-slate-500">{DESTINATION_TYPES[viewingDelivery.destinationType]}</p>
                </div>
                <span className={`px-3 py-1 rounded-full ${DELIVERY_STATUS[viewingDelivery.status]?.color}`}>
                  {DELIVERY_STATUS[viewingDelivery.status]?.label}
                </span>
              </div>
              
              {viewingDelivery.destinationAddress && (
                <div>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <MapPin size={14} /> Address
                  </p>
                  <p>{viewingDelivery.destinationAddress}</p>
                </div>
              )}
              
              {viewingDelivery.contactPerson && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Contact Person</p>
                    <p>{viewingDelivery.contactPerson}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Phone size={14} /> Number
                    </p>
                    <p>{viewingDelivery.contactNumber}</p>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-sm text-slate-500 mb-2">Items</p>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {viewingDelivery.items?.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{item.productName}</span>
                      <span className="font-medium">{item.quantity} {item.unit}s</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Delivery Date</p>
                  <p>{viewingDelivery.deliveryDate}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Delivery Fee</p>
                  <p>₱{(viewingDelivery.deliveryFee || 0).toLocaleString()}</p>
                </div>
              </div>
              
              {viewingDelivery.notes && (
                <div>
                  <p className="text-sm text-slate-500">Notes</p>
                  <p className="text-sm">{viewingDelivery.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASDeliveries;
